import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getGnrPeriodLabel, gnrPeriodEnum, gnrStateEnum } from '../../../../constants';
import { useCreateGnrRequestMutation, useUpdateGnrRequestMutation } from '../../../../hooks/mutations/useGnrRequestMutation';
import { useSitesQuery } from '../../../../hooks/queries/useSitesQuery';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { GnrRequest } from '../../../../services/apiSLTP/sites/gnrRequests';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getDateXDaysAfter, getTomorrowDate } from '../../../../utils/dates';
import CustomDateInput from '../../../ui/CustomDateInput';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageGnrRequestModalProps extends ModalProps {
    gnrRequest: GnrRequest | null;
    refetch: () => void;
}

const ManageGnrRequestModal: React.FC<ManageGnrRequestModalProps> = ({ gnrRequest, open, handleClose, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });
    const [selectedUser, setSelectedUser] = useState<OptionType | null>(null);
    const [selectedSite, setSelectedSite] = useState<OptionType | null>(null);
    const [selectedDate, setSelectedDate] = useState(
        gnrRequest?.plannedDate ? new Date(gnrRequest.plannedDate) : getTomorrowDate().getDay() === 6 ? getDateXDaysAfter(3) : getTomorrowDate()
    );
    const [selectedPeriod, setSelectedPeriod] = useState({
        label: capitalizeFirstLetter(t(getGnrPeriodLabel(gnrRequest?.plannedPeriod ? gnrRequest?.plannedPeriod : gnrPeriodEnum.MORNING))),
        value: gnrRequest?.plannedPeriod ? gnrRequest?.plannedPeriod.toString() : gnrPeriodEnum.MORNING.toString(),
    });
    const [selectedVolume, setSelectedVolume] = useState<string>(gnrRequest?.plannedVolume ? gnrRequest?.plannedVolume.toString() : '');

    const { data: dataSites, isLoading: isSitesLoading } = useSitesQuery({ queries: { active: true } });

    const { mutate: mutateGnrRequest } = useUpdateGnrRequestMutation({
        onSettled: refetch,
        onError: (error) => console.error('Error updating gnrRequest:', error.message),
    });

    const { mutate: addGnrRequest } = useCreateGnrRequestMutation({
        onSettled: refetch,
        onError: (error) => console.error('Error adding gnrRequest:', error.message),
    });

    const handleSubmit = () => {
        if (!selectedSite || !selectedUser) return;
        if (gnrRequest) {
            mutateGnrRequest({
                gnrRequestId: gnrRequest?._id,
                data: {
                    requestDate: selectedDate,
                    requesterId: selectedUser?.value || user?._id || '',
                    requestPeriod: parseInt(selectedPeriod.value),
                    requestVolume: parseFloat(selectedVolume.replace(',', '.')) || 0,
                    siteId: selectedSite.value,
                },
            });
        } else {
            addGnrRequest({
                requestDate: selectedDate,
                requesterId: selectedUser?.value || user?._id || '',
                requestPeriod: parseInt(selectedPeriod.value),
                requestVolume: parseFloat(selectedVolume.replace(',', '.')) || 0,
                siteId: selectedSite.value,
                state: gnrStateEnum.REQUESTED,
            });
        }
    };

    useEffect(() => {
        if (gnrRequest && dataUsers && dataSites) {
            if (!selectedUser) {
                const userFromRequest = dataUsers.find((u) => u._id === gnrRequest.requesterId._id);
                if (userFromRequest) {
                    setSelectedUser({
                        value: userFromRequest._id,
                        label: formatName(userFromRequest.firstName, userFromRequest.lastName),
                    });
                }
            }

            if (!selectedSite) {
                const siteFromRequest = dataSites.find((s) => s._id === gnrRequest.siteId._id);
                if (siteFromRequest) {
                    setSelectedSite({
                        value: siteFromRequest._id,
                        label: siteFromRequest.name,
                    });
                }
            }
        }
    }, [gnrRequest, dataUsers, dataSites]);

    return (
        <ModalBody
            title={t('gnrRequest')}
            subtitle={gnrRequest ? t('update') : t('add')}
            disableSubmit={!(selectedVolume?.length && parseInt(selectedVolume) && selectedSite?.value.length && selectedPeriod.value.length)}
            open={open}
            handleClose={handleClose}
            onSubmit={handleSubmit}
            showSecondaryButton={[gnrStateEnum.REQUESTED, gnrStateEnum.IN_PROGRESS].includes(gnrRequest?.state || -1)}
            secondaryButtonText={t('delete')}
            onSecondaryAction={() => mutateGnrRequest({ gnrRequestId: gnrRequest?._id || '', data: { state: gnrStateEnum.CANCELED } })}
        >
            <>
                <DropDown
                    selectedItem={selectedSite}
                    items={dataSites?.map((site) => ({ label: site.name, value: site._id })) || []}
                    handleChange={(event, value) => setSelectedSite({ value: value?.value || '', label: value?.label || '' })}
                    inputLabel={t('site')}
                    loading={isSitesLoading}
                    required
                />
                <CustomTextInput
                    value={selectedVolume}
                    label={t('volume')}
                    onChange={(text) => setSelectedVolume(text)}
                    errorText={t('error')}
                    type="number"
                    required
                />
                <CustomDateInput
                    label={t('deliveredDate')}
                    onChange={(value) => (value ? setSelectedDate(new Date(value?.toISOString())) : null)}
                    required
                    minDate={new Date()}
                    defaultDate={selectedDate}
                />
                <DropDown
                    selectedItem={selectedPeriod}
                    items={
                        Object.values(gnrPeriodEnum).map((period) => ({
                            label: capitalizeFirstLetter(t(getGnrPeriodLabel(period))),
                            value: period.toString(),
                        })) || []
                    }
                    handleChange={(event, value) => setSelectedPeriod({ value: value?.value || '', label: value?.label || '' })}
                    inputLabel={t('period')}
                    loading={isSitesLoading}
                    sortItems={false}
                    required
                />
                <DropDown
                    selectedItem={selectedUser}
                    items={
                        dataUsers?.map((user) => ({
                            label: formatName(user.firstName, user.lastName),
                            value: user._id,
                        })) || []
                    }
                    handleChange={(event, value) => setSelectedUser(value)}
                    inputLabel={t('person')}
                    loading={isUsersLoading}
                />
            </>
        </ModalBody>
    );
};

export default ManageGnrRequestModal;
